<template>
  <template v-if="typeContextCourant === TypesContextesAutorisesEnum.Prestataire">
    <BasicText v-if="model?.estSousMandatGestion">
      En tant que prestataire ,avec mandat de gestion, l’employeur d’un apprenti vous autorise à saisir et transmettre
      en ligne le contrat d’apprentissage et les pièces nécessaires directement à AKTO pour son compte.
    </BasicText>
    <BasicText v-else>
      En tant que prestataire vous pouvez pré-saisir une demande de formation et la soumettre à l’employeur pour
      validation.
    </BasicText>
    <template v-if="model?.estSousMandatGestion">
      <BasicText>A titre d’exemple, un article à faire figurer dans la convention de formation :</BasicText>
      <div class="introduction__frame">
        <BasicText
          size="body-m"
          font-style="italic"
        >
          Par la présente convention, l’entreprise signataire donne mandat au CFA signataire, qui l’accepte, pour
          accomplir toutes formalités nécessaires aux opérations prévues à l’article L. 6224-1 du code du travail.
        </BasicText>
        <BasicText
          size="body-m"
          font-style="italic"
        >
          *Pour autant, l'entreprise reste l'employeur de l'apprenti et est garante du respect de la réglementation
          associée au contrat d'apprentissage à son égard.
        </BasicText>
        <BasicText
          size="body-m"
          font-style="italic"
        >
          En considération de l’exécution de son mandat, le CFA mandataire ne reçoit aucune rémunération du mandant,
          ledit mandat devant être accompli à titre gratuit.
        </BasicText>
        <BasicText
          size="body-m"
          font-style="italic"
        >
          Le mandant s’engage, envers le mandataire, à lui fournir l’ensemble des informations nécessaires à la bonne
          exécution de son mandat. Il lui donne pouvoir aux fins de le représenter, notamment auprès des opérateurs
          prévus à l’article L. 6332-1 du code du travail.
        </BasicText>
        <BasicText
          size="body-m"
          font-style="italic"
        >
          Le mandataire s’engage à exécuter personnellement son mandat, et ce dans le meilleur intérêt du mandant, et de
          ne pas agir dans son propre intérêt, ni celui d’un tiers. Il agit dans la limite des pouvoirs qui lui sont
          conférés par la présente convention et des règles en vigueur relatives aux opérations prévues à l’article L.
          6224-1 du code du travail. Il s’engage également à informer le mandant de toute situation le justifiant, ou à
          la demande de ce dernier, de l’état d’exécution du mandat, ainsi que de sa pleine réalisation.
        </BasicText>
        <BasicText
          size="body-m"
          font-style="italic"
        >
          En cas de mauvaise exécution de la mission qui lui est confiée, le mandataire engage sa responsabilité devant
          le mandant.
        </BasicText>
        <BasicText
          size="body-m"
          font-style="italic"
        >
          Chacune des parties peut, par voie d’avenant à la présente convention, mettre fin au mandat. En cas de
          différend, l’article 9 de la présente convention s’applique.
        </BasicText>
      </div>
    </template>

    <template v-else>
      <BasicText>
        Pour votre information, l'adresse e-mail de l'adhérent est indispensable lors de la saisie de la demande. Elle
        permettra l'envoi d'un e-mail informant l'adhérent de la pré-saisie de la demande de formation. Celui-ci pourra
        ainsi vérifier l'exactitude des données saisies et aura la possibilité d'accepter ou de refuser la demande.
      </BasicText>
      <BasicText>
        Si la demande est acceptée et transmise à AKTO, vous recevrez une notification d’accord. En revanche, si
        l'adhérent refuse de reprendre la demande, vous en serez informée.
      </BasicText>
    </template>

    <BasicText>
      Pour vous aider dans la compréhension du dispositif et la saisie de la demande, vous trouverez ci-dessous des
      liens utiles.
    </BasicText>

    <BasicText>
      <ul>
        <li
          v-for="(urlLabel, i) in urlLabels"
          :key="i"
        >
          <BasicLink
            :label="urlLabel.label"
            @link-click="redirectExterne(urlLabel.url)"
          />
        </li>
      </ul>
    </BasicText>
  </template>
  <template v-else>
    <BasicText>
      Avant de conclure un contrat d’apprentissage, renseignez-vous sur les règles applicables ainsi que les
      dispositions particulières prévues par votre convention collective ou les accords collectifs de branche (Exemple :
      la rémunération, la durée maximale du contrat ou bien le niveau de prise en charge de la formation). N’hésitez pas
      à vous rapprocher de votre conseiller.
    </BasicText>
    <BasicText>
      Pour vous aider dans la compréhension du dispositif et la saisie de la demande, vous trouverez ci-dessous des
      liens utiles.
    </BasicText>
    <BasicText>
      <ul>
        <li
          v-for="(urlLabel, i) in urlLabels"
          :key="i"
        >
          <BasicLink
            :label="urlLabel.label"
            @link-click="redirectExterne(urlLabel.url)"
          />
        </li>
      </ul>
    </BasicText>
  </template>
  <WrapperCheckbox
    v-model="validationNotice"
    :alertLabel="meta.validationNotice?.$messageErreurDirty!"
    :readonly
  >
    <BasicText>
      J'ai pris connaissance de la
      <BasicLink
        label="notice"
        @link-click="redirectExterne('https://www.formulaires.service-public.fr/gf/getNotice.do?cerfaNotice=51649&cerfaFormulaire=10103')"
      />
      comprenant les informations liées au traitement des données à caractère personnel.
    </BasicText>
  </WrapperCheckbox>
</template>

<script setup lang="ts">
  import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import type { UrlLabel } from '@/features/interface/shared/tools'
import { redirectExterne } from '@/features/shared/tools/url'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useDemandeCAPPStore } from '../../useDemandeCAPPStore'

  const store = useDemandeCAPPStore()
  const { model, typeContextCourant, meta, readonly } = storeToRefs(store)

  const { setupFieldWithKey } = store

  const validationNotice = setupFieldWithKey<boolean>('validationNotice')

  const urlLabels = computed(() => {
    const urlsLabels: UrlLabel[] = []
    if (typeContextCourant.value === TypesContextesAutorisesEnum.Adherent) {
      urlsLabels.push({
        label: 'Les règles de prise en charge',
        url: 'https://www.akto.fr/financer-une-formation/regles-de-prise-en-charge/',
      })
      urlsLabels.push({
        label: 'Le coût d’un contrat et rémunération',
        url: 'https://www.akto.fr/recruter-alternance/calcul-cout-contrat-apprentissage/',
      })
      urlsLabels.push({
        label: 'Maitre d’apprentissage et critères d’éligibilité',
        url: 'https://www.akto.fr/recruter-en-alternance/le-tutorat/',
      })
      urlsLabels.push({
        label: "Contrat d'apprentissage site AKTO",
        url: 'https://www.akto.fr/recruter-alternance/contrat-apprentissage/',
      })
      urlsLabels.push({
        label: 'Le précis de l’apprentissage',
        url: 'https://travail-emploi.gouv.fr/demarches-ressources-documentaires/documentation-et-publications-officielles/guides/precis-apprentissage',
      })
      urlsLabels.push({
        label: 'Le mode opératoire',
        url: 'https://ressourcesstatiques.akto.fr/guide/v2/GuideContratApprentissage.pdf',
      })
    }

    if (typeContextCourant.value === TypesContextesAutorisesEnum.Prestataire) {
      if (model.value?.estSousMandatGestion === true) {
        urlsLabels.push({
          label: 'Le mandat de gestion, comment ça fonctionne ?',
          url: 'https://www.akto.fr/breve/nouveau-le-cfa-peut-gerer-le-depot-des-contrats-dapprentissage-pour-le-compte-de-lentreprise/',
        })

        urlsLabels.push({
          label: 'Le guide à destination des CFA',
          url: 'https://www.akto.fr/le-saviez-vous-une-nouvelle-version-du-vade-mecum-cfa-est-en-ligne/',
        })
      }

      urlsLabels.push({
        label: 'Le mode opératoire saisie OF avec et sans mandat',
        url: 'https://ressourcesstatiques.akto.fr/guide/v2/GuideContratApprentissagePrestataire.pdf',
      })
    }
    return urlsLabels
  })
</script>

<style scoped lang="less">
  .introduction__frame {
    border-style: solid;
    border-color: #00a79b;
    padding: 1rem;
    border-width: thin;
  }
</style>
